<template>
  <section>
    <div class="filters">
      <product-filter v-for="filter in availableFilters"
        v-bind:key="filter.label"
        :name="filter.label"
        :label="filter.label"
        :options="filter.options"
        :active="activeFilters"
        :related="filter.related"
        @onSelectionChanged="updateFilters"
      />
    </div>
    <div class="grid">
      <figure v-for="entry in entries" v-bind:key="entry.id" class="gallery-items" @click="openModal(entry, false)" @keyup.enter="openModal(entry, false)" tabindex="0">
        <img :src="entry.image_src || entry.image" :sizes="sizes" :srcset="entry.srcset" :alt="entry.title" />
        <figcaption>
          <h4>{{ entry.title }}</h4>
          <span v-if="entry.associations && entry.associations.length">{{ entry.associations[0].title }}</span>
        </figcaption>
      </figure>
    </div>
    <div class="more" v-if="!isLoading && !hideLoadButton">
      <load-more
        :is-loading="isLoading"
        :pagination="pagination"
        @loadMore="getEntries(true)">
      </load-more>
    </div>
    <div class="notification" v-if="notificationTriggered" v-bind:class="{visible: notificationVisible}" @click="dismissNotification()">
      <p>{{ notificationText }}</p>
      <button type="button" @click="dismissNotification()"></button>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import LoadMore from '../atoms/load_more.js';
import ProductFilter from '../atoms/product_filter.js';
import EventBus from '../event_bus';
import { INSPIRATION_GALLERY_ENDPOINT, INSPIRATION_GALLERY_LIMIT } from '../constants';

export default {
  components: {
    LoadMore,
    ProductFilter
  },
  props: [
    'preload',
    'filters',
    'sizes',
    'widths',
    'elementType',
    'section',
    'limit'
  ],
  data: function() {
    return {
      activeFilters: {
        to: [],
        through: [],
      },
      settings: {},
      entries: [],
      currentEntries: [],
      pagination: {},
      isLoading: true,
      hideLoadButton: false,
      notificationText: null,
      notificationTriggered: false,
      notificationVisible: false,
      urlParamCollectionIds: null,
      urlParamElementId: null,
    }
  },
  created() {
    this.setFiltersByUrlParams();
  },
  computed: {
    availableFilters: function() {
      return JSON.parse(this.filters);
    },
    relatedTo: function() {
      return this.activeFilters.to;
    },
    relatedThrough: function() {
      return this.activeFilters.through;
    },
    collectionIds: function() {
      return this.currentEntries.map(x => parseInt(x.id));
    }
  },
  mounted() {
    const params = new URLSearchParams(location.search);
    const viewElement = params.get("viewElement");
    const viewCollectionStr = params.get("viewCollection");

    if (viewCollectionStr) {
      this.urlParamElementId = viewElement;
      this.urlParamCollectionIds = viewCollectionStr.split(',');

      this.openModal(null, true);
    } else {
      this.getEntries();
    }
  },
  methods: {
    setFiltersByUrlParams: function() {
      const urlParams = new URLSearchParams(window.location.search);
      const filtersMap = {
        family: 'family',
        type: 'type',
        spaces: 'spaces',
      };

      Object.keys(filtersMap).forEach((key) => {
        const value = urlParams.get(key);

        if (value) {
          this.availableFilters.forEach((filter) => {
            if (filter.label.toLowerCase() === filtersMap[key]) {
              const option = filter.options.find(option => option.title.toLowerCase() === value.toLowerCase());
              if (option) {
                this.activeFilters[filter.related].push(option.id);
                option.selected = true;
              }
            }
          });
        }
      });
    },
    updateFilters: function (obj) {
      this.activeFilters[obj.related] = this.activeFilters[obj.related].filter(function(value) {
        return value != obj.prev;
      });

      const filtersArray = ['family', 'type', 'spaces'];

      // Make sure an ID was passed.
      if (!isNaN(obj.next)) {
        this.activeFilters[obj.related].push(obj.next);

        /* update URL params */
        const urlParams = new URLSearchParams(window.location.search);

        filtersArray.forEach((filterKey) => {
          this.availableFilters.forEach((filter) => {
            if (filter.label.toLowerCase() === filterKey) {
              const option = filter.options.find(option => parseInt(option.id) === parseInt(obj.next));

              if (option) {
                urlParams.set(filter.label.toLowerCase(), option.title.toLowerCase());
                window.location.search = urlParams.toString();
              }
            }
          });
        });
      } else {
        /* clear out this specific filter */
        const urlParams = new URLSearchParams(window.location.search);

        this.availableFilters.forEach((filter) => {
          if (filter.related === obj.related) {
            urlParams.delete(filter.label.toLowerCase());
            window.location.search = urlParams.toString();
          }
        });
      }
    },
    getEntries: async function(loadMore=false) {
      this.isLoading = true;
      let endpoint = INSPIRATION_GALLERY_ENDPOINT;

      if (loadMore && this.pagination.hasOwnProperty("links") && this.pagination.links.hasOwnProperty("next")) {
        endpoint = this.pagination.links.next;
      }

      const params = {
        section: this.section,
        relatedTo: this.activeFilters['to'],
        relatedThrough: this.activeFilters['through'],
        limit: INSPIRATION_GALLERY_LIMIT,
        widths: this.widths,
      };

      try {
        const {
          data: {
            data, meta
          }
        } = await axios.get(endpoint, { params });

        if(loadMore) {
          const currentEntryIDs = this.entries.map(entry => entry.id);

          data.forEach((newEntry) => {
            if (!currentEntryIDs.includes(newEntry.id)) {
              this.entries.push(newEntry);
            }
          })
        } else {
          this.entries = data;
          this.currentEntries = data;
        }

        if (meta.pagination.current_page >= meta.pagination.total_pages) {
          this.hideLoadButton = true;
        }

        this.pagination = meta.pagination;
        this.isLoading = false;

      } catch (error) {
        console.log('Error getting inspirations: ', error);
        this.entries = [];
        this.isLoading = false;
      }
    },
    preloadEntries: function() {
      JSON.parse(this.preload).forEach(function($obj) {
        this.entries.push($obj);
      });
    },
    openModal: function(entry, openOnUrlParams=false) {
      if (openOnUrlParams && !entry) {
        EventBus.$emit('open-modal', this.urlParamElementId, this.urlParamCollectionIds, 'carousel');
      } else {
        this.$router.push({ path: `?viewElement=${entry.id}&viewCollection=${this.collectionIds}` });
        this.setEntriesWithinBounds(entry);
        EventBus.$emit('open-modal', entry.id, this.collectionIds, 'carousel');
      }
    },
    closeLoading: function() {
      this.isLoading = false;
    },
    cancelModal: function() {
      this.closeLoading();
      this.$emit('close-modal');
      this.showNotification('There was an issue loading the item');
    },
    setEntriesWithinBounds: function(entry) {
      const idx = this.entries.indexOf(entry);
      const distanceFromEnd = this.entries.length - idx;
      let selectedEntries = [];
      let start = -6;
      let end = 6;

      if (distanceFromEnd < 6) {
        start = (-12 + distanceFromEnd);
        end = (12 + start);

        for (let i=start; i<end; i++) {
          selectedEntries.push(this.entries[idx + i]);
        }
      } else if (idx < 7) {
        selectedEntries = this.entries.slice(0, 12);
      } else {
        for (let i=start; i<end; i++) {
          selectedEntries.push(this.entries[idx + i]);
        }
      }

      this.currentEntries = selectedEntries.filter(entry => entry);
    },
    showNotification: function(text) {
      this.notificationText = text;
      this.notificationTriggered = true;
      setTimeout(() => this.notificationVisible = true, 250);
    },
    dismissNotification: function() {
      this.notificationVisible = false;
      setTimeout(() => {
        this.notificationText = null;
        this.notificationTriggered = false;
      }, 500);
    }
  },
  watch: {
    relatedTo: function(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getEntries();
      }
    },
    relatedThrough: function(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.getEntries();
      }
    }
  },
}
</script>
